import React, { useState } from "react";

import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";

import {
    ConsentStatusChip,
    MESSAGE_LIMITS,
    formatDateTime,
    formatPhone,
    getErrorMessage,
    formatDateForApi,
} from "./reportShared";


export default function InboundMessagesReport({
                                                  apiUrl,
                                                  getAccessToken,
                                              }) {
    /*
     * ---------------------------------------------------------
     * Standard report state
     * ---------------------------------------------------------
     */

    const [limit, setLimit] = useState(50);
    const [offset, setOffset] = useState(0);

    const [report, setReport] = useState(null);

    const [loading, setLoading] = useState(false);

    /*
     * ---------------------------------------------------------
     * CSV export state
     * ---------------------------------------------------------
     */

    const today = new Date();

    const defaultStopDate = formatDateForApi(today);

    const defaultStart = new Date();
    defaultStart.setDate(defaultStart.getDate() - 6);

    const defaultStartDate =
        formatDateForApi(defaultStart);

    const [csvStartDate, setCsvStartDate] =
        useState(defaultStartDate);

    const [csvStopDate, setCsvStopDate] =
        useState(defaultStopDate);

    const [downloadingCsv, setDownloadingCsv] =
        useState(false);

    const [error, setError] = useState("");


    /*
     * ---------------------------------------------------------
     * Load paginated report
     * ---------------------------------------------------------
     */

    const load = async (nextOffset = offset) => {
        setLoading(true);
        setError("");

        try {
            const token = await getAccessToken();

            const params = new URLSearchParams({
                limit: String(limit),
                offset: String(nextOffset),
            });

            const response = await fetch(
                `${apiUrl}/consent/enriched-inbound-messages?${params}`,
                {
                    headers: {
                        Accept: "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const body =
                await response.json().catch(() => null);

            if (!response.ok) {
                throw new Error(
                    body?.details ||
                    body?.error ||
                    body?.message ||
                    `Unable to load inbound messages. HTTP ${response.status}.`
                );
            }

            setReport({
                ...body,
                messages: Array.isArray(body?.messages)
                    ? body.messages
                    : [],
            });

            setOffset(nextOffset);

        } catch (e) {
            setError(
                getErrorMessage(
                    e,
                    "Unable to load inbound messages."
                )
            );

        } finally {
            setLoading(false);
        }
    };


    /*
     * ---------------------------------------------------------
     * Download CSV
     * ---------------------------------------------------------
     */

    const downloadCsv = async () => {
        setError("");

        if (!csvStartDate || !csvStopDate) {
            setError(
                "Please select a start date and end date."
            );
            return;
        }

        if (csvStartDate > csvStopDate) {
            setError(
                "Start date cannot be after end date."
            );
            return;
        }

        setDownloadingCsv(true);

        try {
            const token = await getAccessToken();

            const params = new URLSearchParams({
                start: csvStartDate,
                stop: csvStopDate,
            });

            const response = await fetch(
                `${apiUrl}/consent/enriched-inbound-messages/csv?${params}`,
                {
                    headers: {
                        Accept: "text/csv",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                const body =
                    await response.json().catch(() => null);

                throw new Error(
                    body?.details ||
                    body?.error ||
                    body?.message ||
                    `Unable to download CSV. HTTP ${response.status}.`
                );
            }

            const blob = await response.blob();

            const url =
                window.URL.createObjectURL(blob);

            const link =
                document.createElement("a");

            link.href = url;

            link.download =
                `inbound-messages-${csvStartDate}-to-${csvStopDate}.csv`;

            document.body.appendChild(link);

            link.click();

            link.remove();

            window.URL.revokeObjectURL(url);

        } catch (e) {
            setError(
                getErrorMessage(
                    e,
                    "Unable to download inbound messages CSV."
                )
            );

        } finally {
            setDownloadingCsv(false);
        }
    };


    /*
     * ---------------------------------------------------------
     * Pagination
     * ---------------------------------------------------------
     */

    const messages =
        report?.messages || [];

    const page =
        Math.floor(offset / limit) + 1;

    const canNext =
        typeof report?.pagination?.has_next === "boolean"
            ? report.pagination.has_next
            : messages.length === limit;


    /*
     * ---------------------------------------------------------
     * Render
     * ---------------------------------------------------------
     */

    return (
        <Stack spacing={2.5}>

            {/* CSV Export */}

            <Paper
                variant="outlined"
                sx={{
                    p: 2,
                    backgroundColor: "#fafafa",
                }}
            >
                <Stack spacing={2}>

                    <Box>
                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 700 }}
                        >
                            Download Inbound Messages
                        </Typography>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            Download inbound messages for a selected
                            date range as a CSV file.
                        </Typography>
                    </Box>

                    <Stack
                        direction={{
                            xs: "column",
                            md: "row",
                        }}
                        spacing={2}
                        alignItems={{
                            xs: "stretch",
                            md: "center",
                        }}
                    >

                        <TextField
                            label="Start Date"
                            type="date"
                            value={csvStartDate}
                            onChange={(e) =>
                                setCsvStartDate(
                                    e.target.value
                                )
                            }
                            InputLabelProps={{
                                shrink: true,
                            }}
                            sx={{
                                width: {
                                    xs: "100%",
                                    md: 200,
                                },
                            }}
                        />

                        <TextField
                            label="End Date"
                            type="date"
                            value={csvStopDate}
                            onChange={(e) =>
                                setCsvStopDate(
                                    e.target.value
                                )
                            }
                            InputLabelProps={{
                                shrink: true,
                            }}
                            sx={{
                                width: {
                                    xs: "100%",
                                    md: 200,
                                },
                            }}
                        />

                        <Button
                            variant="outlined"
                            onClick={downloadCsv}
                            disabled={downloadingCsv}
                            startIcon={
                                downloadingCsv
                                    ? (
                                        <CircularProgress
                                            size={18}
                                        />
                                    )
                                    : null
                            }
                            sx={{
                                minHeight: 56,
                                px: 3,
                            }}
                        >
                            {downloadingCsv
                                ? "Preparing CSV"
                                : "Download CSV"}
                        </Button>

                    </Stack>

                </Stack>
            </Paper>


            {/* Errors */}

            {error && (
                <Alert severity="error">
                    {error}
                </Alert>
            )}


            {/* Messages Per Page */}

            <Box sx={{ maxWidth: 240 }}>
                <FormControl fullWidth>
                    <InputLabel>
                        Messages Per Page
                    </InputLabel>

                    <Select
                        label="Messages Per Page"
                        value={limit}
                        onChange={(e) => {
                            setLimit(
                                Number(e.target.value)
                            );

                            setOffset(0);
                            setReport(null);
                        }}
                    >
                        {MESSAGE_LIMITS.map((x) => (
                            <MenuItem
                                key={x}
                                value={x}
                            >
                                {x}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>


            {/* Run Report */}

            <Box>
                <Button
                    variant="contained"
                    onClick={() => load(0)}
                    disabled={loading}
                    startIcon={
                        loading
                            ? (
                                <CircularProgress
                                    size={18}
                                />
                            )
                            : null
                    }
                >
                    {loading
                        ? "Running Report"
                        : "Run Inbound Messages Report"}
                </Button>
            </Box>


            {/* Results */}

            {report && (
                <Stack spacing={2}>

                    <Paper
                        variant="outlined"
                        sx={{
                            display: "flex",
                            gap: 3,
                            p: 2,
                        }}
                    >
                        <Box>
                            <Typography
                                color="text.secondary"
                                variant="body2"
                            >
                                Messages Shown
                            </Typography>

                            <Typography variant="h4">
                                {messages.length.toLocaleString()}
                            </Typography>
                        </Box>

                        <Box>
                            <Typography
                                color="text.secondary"
                                variant="body2"
                            >
                                Page
                            </Typography>

                            <Typography variant="h5">
                                {page}
                            </Typography>
                        </Box>
                    </Paper>


                    <TableContainer
                        component={Paper}
                        variant="outlined"
                        sx={{
                            maxHeight: "55vh",
                        }}
                    >
                        <Table
                            stickyHeader
                            size="small"
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        Received
                                    </TableCell>

                                    <TableCell>
                                        Patient
                                    </TableCell>

                                    <TableCell>
                                        Patient ID
                                    </TableCell>

                                    <TableCell>
                                        Facility
                                    </TableCell>

                                    <TableCell>
                                        Consent Status
                                    </TableCell>

                                    <TableCell>
                                        From
                                    </TableCell>

                                    <TableCell>
                                        To
                                    </TableCell>

                                    <TableCell>
                                        Message
                                    </TableCell>

                                    <TableCell>
                                        Processing Status
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>

                                {messages.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={9}
                                            align="center"
                                            sx={{ py: 4 }}
                                        >
                                            No inbound messages
                                            were found.
                                        </TableCell>
                                    </TableRow>

                                ) : (
                                    messages.map(
                                        (m, i) => (
                                            <TableRow
                                                key={
                                                    m?.id ||
                                                    m?.message_sid ||
                                                    i
                                                }
                                                hover
                                            >
                                                <TableCell>
                                                    {formatDateTime(
                                                        m?.received_on
                                                    )}
                                                </TableCell>

                                                <TableCell>
                                                    {m?.patient_name ||
                                                        "Unmatched"}
                                                </TableCell>

                                                <TableCell>
                                                    {m?.patient_id ||
                                                        ""}
                                                </TableCell>

                                                <TableCell>
                                                    {m?.facility_name ||
                                                        ""}
                                                </TableCell>

                                                <TableCell>
                                                    <ConsentStatusChip
                                                        status={
                                                            m?.consent_status
                                                        }
                                                    />
                                                </TableCell>

                                                <TableCell>
                                                    {formatPhone(
                                                        m?.from_phone_number
                                                    )}
                                                </TableCell>

                                                <TableCell>
                                                    {formatPhone(
                                                        m?.to_phone_number
                                                    )}
                                                </TableCell>

                                                <TableCell
                                                    sx={{
                                                        minWidth: 240,
                                                        maxWidth: 500,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            whiteSpace:
                                                                "pre-wrap",
                                                            overflowWrap:
                                                                "anywhere",
                                                        }}
                                                    >
                                                        {m?.message_body ||
                                                            ""}
                                                    </Typography>
                                                </TableCell>

                                                <TableCell>
                                                    {m?.processing_status ||
                                                        ""}
                                                </TableCell>
                                            </TableRow>
                                        )
                                    )
                                )}

                            </TableBody>
                        </Table>
                    </TableContainer>


                    {/* Pagination */}

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent:
                                "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Typography
                            color="text.secondary"
                        >
                            Page {page}
                        </Typography>

                        <Stack
                            direction="row"
                            spacing={1}
                        >
                            <Button
                                variant="outlined"
                                disabled={
                                    loading ||
                                    offset === 0
                                }
                                onClick={() =>
                                    load(
                                        Math.max(
                                            0,
                                            offset - limit
                                        )
                                    )
                                }
                            >
                                Previous
                            </Button>

                            <Button
                                variant="outlined"
                                disabled={
                                    loading ||
                                    !canNext
                                }
                                onClick={() =>
                                    load(
                                        offset + limit
                                    )
                                }
                            >
                                Next
                            </Button>
                        </Stack>
                    </Box>

                </Stack>
            )}

        </Stack>
    );
}